import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Paperclip,
} from 'lucide-react';
import { staffClient } from 'clients/staff';
import Table, { Column } from 'ui/Table';
import StatCard from 'ui/StatCard';
import StatsGrid from 'ui/StatsGrid';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import FilterSection from 'ui/FilterSection';
import TableContainer from 'ui/TableContainer';
import SearchInput from 'ui/SearchInput';
import Loading from 'ui/Loading';
import Button from 'ui/Button';
import Chip, { ChipGroup } from 'ui/Chip';
import { Badge } from 'ui/shadcn';
import { cn } from 'lib/utils';
import { DocumentTextIcon, InboxIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

interface Article {
    id: number;
    title: string;
    writer: {
        name: string;
        user_id: string;
        user_type: string;
    };
    created_at: string;
    category?: {
        id: number;
        name: string;
    };
    board: {
        id: number;
        name: string;
        description: string;
    };
    is_public?: boolean;
    files?: any[];
}

interface BoardData {
    id: number;
    name: string;
    description: string;
    visible: boolean;
    categories: Array<{
        id: number;
        name: string;
    }>;
}

// 7일 이내 게시글인지 확인
const isNewArticle = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = now.getTime() - created.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
};

const DynamicBoard: React.FC = () => {
    const { boardId } = useParams<{ boardId: string }>();
    const navigate = useNavigate();
    const [board, setBoard] = useState<BoardData | null>(null);
    const [articles, setArticles] = useState<Article[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('전체');
    const [sortBy, setSortBy] = useState('최신순');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const postsPerPage = 10;

    const fetchBoardData = useCallback(async () => {
        try {
            // ID로 게시판 직접 조회
            const ret = await staffClient.get(`/board/${boardId}`);
            const boardData = ret?.data;

            if (boardData) {
                setBoard(boardData);
            } else {
                console.error('Board not found:', boardId);
                navigate('/'); // 게시판을 찾을 수 없으면 메인으로 이동
            }
        } catch (error) {
            console.error('Error fetching board data:', error);
            // 게시판을 찾을 수 없을 경우 메인으로 이동
            if ((error as any)?.response?.status === 404) {
                navigate('/');
            }
            setLoading(false);
        }
    }, [boardId, navigate]);

    const fetchArticles = useCallback(async () => {
        if (!board) return;

        try {
            setLoading(true);
            const params: any = {
                boardId: board.id,
                offset: (currentPage - 1) * postsPerPage,
                limit: postsPerPage,
            };

            if (categoryFilter !== '전체') {
                const category = board.categories?.find(cat => cat.name === categoryFilter);
                if (category) {
                    params.categoryId = category.id;
                }
            }

            if (searchTerm) {
                params.searchText = searchTerm;
            }

            const ret = await staffClient.get('/article', { params });
            const articlesData = ret?.data?.data || [];
            const meta = ret?.data?.meta || {};

            setArticles(articlesData);
            setTotalPages(Math.ceil(meta.total / postsPerPage));
            setTotalCount(meta.total || 0);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching articles:', error);
            setLoading(false);
        }
    }, [board, currentPage, categoryFilter, searchTerm, postsPerPage]);

    useEffect(() => {
        if (boardId) {
            setIsAnimating(true);
            fetchBoardData();
        }
    }, [boardId, fetchBoardData]);

    useEffect(() => {
        if (board) {
            fetchArticles();
        }
    }, [board, fetchArticles]);

    const handleSearch = () => {
        setCurrentPage(1);
        fetchArticles();
    };

    const handleCategoryChange = (category: string) => {
        setCategoryFilter(category);
        setCurrentPage(1);
    };

    const handleWriteClick = () => {
        // ArticleEditor로 이동 (새 글 작성)
        navigate(`/boards/${boardId}/articles/editor`);
    };

    const handlePostClick = (article: Article) => {
        // ArticleViewer로 이동 (게시물 보기)
        navigate(`/boards/${boardId}/articles/${article.id}`);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    // Table columns definition
    const columns: Column<Article>[] = [
        {
            key: 'id',
            header: '번호',
            width: '70px',
            align: 'center',
            render: (value) => (
                <span className="text-muted-foreground tabular-nums">{value}</span>
            )
        },
        {
            key: 'title',
            header: '제목',
            render: (_, article) => (
                <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{article.title}</span>
                    {article.files && article.files.length > 0 && (
                        <Paperclip className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                    )}
                    {isNewArticle(article.created_at) && (
                        <span className="inline-flex h-4 shrink-0 items-center justify-center rounded bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
                            N
                        </span>
                    )}
                </div>
            )
        },
        {
            key: 'writer',
            header: '작성자',
            width: '120px',
            render: (_, article) => (
                <span className="text-muted-foreground">
                    {article.writer?.name || '익명'}
                </span>
            )
        },
        {
            key: 'category',
            header: '카테고리',
            width: '110px',
            render: (_, article) =>
                article.category?.name ? (
                    <Badge variant="default">{article.category.name}</Badge>
                ) : (
                    <Badge variant="secondary">일반</Badge>
                )
        },
        {
            key: 'is_public',
            header: '공개',
            width: '80px',
            align: 'center',
            render: (_, article) =>
                article.is_public !== false ? (
                    <Badge variant="success">공개</Badge>
                ) : (
                    <Badge variant="secondary">비공개</Badge>
                )
        },
        {
            key: 'created_at',
            header: '작성일',
            width: '110px',
            align: 'center',
            sortable: true,
            render: (value) => (
                <span className="text-muted-foreground tabular-nums">
                    {formatDate(value)}
                </span>
            )
        }
    ];

    // 통계 계산
    const stats = {
        totalPosts: articles.length,
        todayPosts: articles.filter(article => {
            const today = new Date().toDateString();
            const articleDate = new Date(article.created_at).toDateString();
            return today === articleDate;
        }).length,
        hotPosts: Math.floor(articles.length * 0.1), // 임시로 10%를 인기글로 설정
        activeUsers: new Set(articles.map(article => article.writer?.user_id)).size
    };

    // 게시판 정보를 받기 전에만 전체 로딩. 이후 페이지·검색 변경 시에는 표 안에서만 로딩 표시
    if (loading && !board) {
        return (
            <PageContainer>
                <Loading text="게시판을 불러오는 중..." />
            </PageContainer>
        );
    }

    if (!board) {
        return (
            <PageContainer>
                <div className="rounded border border-border bg-card py-20 text-center">
                    <InboxIcon className="mx-auto mb-4 h-12 w-12 text-neutral-400" strokeWidth={1.5} />
                    <h2 className="mb-1 text-lg font-semibold text-foreground">게시판을 찾을 수 없습니다</h2>
                    <p className="text-base text-muted-foreground">요청하신 게시판이 존재하지 않거나 삭제되었습니다.</p>
                </div>
            </PageContainer>
        );
    }

    // 페이지 번호 — ui/Table 내장 페이지네이션과 동일한 5칸 윈도우
    const renderPagination = () => {
        if (totalPages <= 1 || articles.length === 0) return null;
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        const pages: number[] = [];
        for (let i = startPage; i <= endPage; i++) pages.push(i);

        const startIdx = (currentPage - 1) * postsPerPage + 1;
        const endIdx = Math.min(currentPage * postsPerPage, totalCount);

        return (
            <div className="flex items-center justify-between gap-3 border-t border-border bg-neutral-50 px-4 py-3">
                <div className="text-xs text-muted-foreground tabular-nums">
                    <span className="font-medium text-foreground">
                        {startIdx}–{endIdx}
                    </span>{' '}
                    / 전체 <span className="font-medium text-foreground">{totalCount}</span>건
                </div>
                <div className="flex items-center gap-1">
                    <PageButton
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        aria-label="처음"
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </PageButton>
                    <PageButton
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        aria-label="이전"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </PageButton>
                    {pages.map((pageNum) => (
                        <PageButton
                            key={pageNum}
                            active={pageNum === currentPage}
                            onClick={() => setCurrentPage(pageNum)}
                        >
                            <span className="tabular-nums">{pageNum}</span>
                        </PageButton>
                    ))}
                    <PageButton
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        aria-label="다음"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </PageButton>
                    <PageButton
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        aria-label="마지막"
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </PageButton>
                </div>
            </div>
        );
    };

    return (
        <PageContainer>
            <PageHeader
                title={board.name}
                subtitle={board.description}
                actions={
                    <Button
                        variant="primary"
                        icon={<PencilSquareIcon />}
                        onClick={handleWriteClick}
                    >
                        글쓰기
                    </Button>
                }
            />

            <StatsGrid columns={4}>
                <StatCard title="총 게시물" value={stats.totalPosts} />
                <StatCard title="오늘 작성" value={stats.todayPosts} />
                <StatCard title="인기글" value={stats.hotPosts} />
                <StatCard title="활성 사용자" value={stats.activeUsers} />
            </StatsGrid>

            <FilterSection className="flex-col items-stretch gap-3">
                <SearchInput
                    value={searchTerm}
                    onChange={(value) => {
                        setSearchTerm(value);
                        if (value === '') {
                            handleSearch();
                        }
                    }}
                    placeholder="제목 또는 작성자 검색..."
                    onEnter={handleSearch}
                />

                {/* 카테고리·정렬 필터 */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    {board.categories && board.categories.length > 0 && (
                        <ChipGroup>
                            <Chip
                                label="전체"
                                active={categoryFilter === '전체'}
                                onClick={() => handleCategoryChange('전체')}
                            />
                            {board.categories.map((category) => (
                                <Chip
                                    key={category.id}
                                    label={category.name}
                                    active={categoryFilter === category.name}
                                    onClick={() => handleCategoryChange(category.name)}
                                />
                            ))}
                        </ChipGroup>
                    )}
                    <ChipGroup className="ml-auto">
                        {['최신순', '인기순', '조회순'].map((s) => (
                            <Chip
                                key={s}
                                label={s}
                                active={sortBy === s}
                                onClick={() => setSortBy(s)}
                            />
                        ))}
                    </ChipGroup>
                </div>
            </FilterSection>

            <TableContainer
                title="게시글 목록"
                resultCount={totalCount}
            >
                {!loading && articles.length === 0 ? (
                    <div className="px-6 py-16 text-center">
                        <DocumentTextIcon className="mx-auto mb-3 h-10 w-10 text-neutral-400" strokeWidth={1.5} />
                        <p className="mb-4 text-base text-muted-foreground">등록된 게시글이 없습니다.</p>
                        <Button
                            variant="outline"
                            icon={<PencilSquareIcon />}
                            onClick={handleWriteClick}
                        >
                            첫 번째 글 작성하기
                        </Button>
                    </div>
                ) : (
                    <>
                        <Table
                            columns={columns}
                            data={articles}
                            pagination={false}
                            loading={loading}
                            stickyHeader
                            maxHeight="600px"
                            onRowClick={handlePostClick}
                        />
                        {!loading && renderPagination()}
                    </>
                )}
            </TableContainer>
        </PageContainer>
    );
};

const PageButton: React.FC<
    React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }
> = ({ active, className, children, ...props }) => (
    <button
        type="button"
        className={cn(
            'inline-flex h-7 min-w-[28px] items-center justify-center rounded border border-transparent px-1.5 text-xs transition-colors duration-fast',
            active
                ? 'bg-primary text-primary-foreground'
                : 'text-neutral-700 hover:bg-muted disabled:text-neutral-400 disabled:hover:bg-transparent',
            'disabled:cursor-not-allowed',
            className
        )}
        {...props}
    >
        {children}
    </button>
);

export default DynamicBoard;
